import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Call } from './useCalls';

interface ScheduledCallsFilters {
  userId?: string;
  contactId?: string;
  leadId?: string;
  dealId?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}

// Helper function to build common query filters
const applyCommonFilters = (
  query: any,
  filters?: ScheduledCallsFilters
) => {
  if (filters?.userId) {
    query = query.eq('handled_by_app_user_id', filters.userId);
  }
  if (filters?.leadId) {
    query = query.eq('lead_id', filters.leadId);
  }
  if (filters?.startDate) {
    query = query.gte('scheduled_for', filters.startDate);
  }
  if (filters?.endDate) {
    query = query.lte('scheduled_for', filters.endDate);
  }
  if (filters?.status) {
    if (filters.status === 'scheduled') {
      query = query.in('status', ['scheduled', 'in-progress']);
    } else {
      query = query.eq('status', filters.status);
    }
  } else {
    query = query.in('status', ['scheduled', 'in-progress', 'completed', 'canceled', 'missed']);
  }
  return query;
};

// Helper to transform handler from array to single object
const transformHandler = (call: any) => ({
  ...call,
  handler: Array.isArray(call.handler) && call.handler.length > 0 
    ? call.handler[0] 
    : call.handler
});

export function useScheduledCalls(filters?: ScheduledCallsFilters) {
  const [scheduledCalls, setScheduledCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadScheduledCalls = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let allCalls: Call[] = [];
      const selectQuery = `
        *,
        handler:app_users!calls_handled_by_app_user_id_fkey(
          display_name,
          email
        )
      `;

      // If we have contactId, fetch by contact_id
      if (filters?.contactId) {
        let contactQuery = supabase
          .from('calls')
          .select(selectQuery)
          .eq('contact_id', filters.contactId)
          .not('scheduled_for', 'is', null)
          .order('scheduled_for', { ascending: true });

        contactQuery = applyCommonFilters(contactQuery, filters);

        const { data, error: contactError } = await contactQuery;
        if (contactError) throw contactError;
        
        allCalls = (data || []).map(transformHandler);
      }

      // If we have dealId, fetch by deal_id and merge (OR logic)
      if (filters?.dealId) {
        let dealQuery = supabase
          .from('calls')
          .select(selectQuery)
          .eq('deal_id', filters.dealId)
          .not('scheduled_for', 'is', null)
          .order('scheduled_for', { ascending: true });

        dealQuery = applyCommonFilters(dealQuery, filters);

        const { data, error: dealError } = await dealQuery;
        if (dealError) throw dealError;

        // Merge without duplicates
        const existingIds = new Set(allCalls.map(c => c.id));
        const newCalls = (data || [])
          .map(transformHandler)
          .filter((c: Call) => !existingIds.has(c.id));
        
        allCalls = [...allCalls, ...newCalls];
      }

      // If neither contactId nor dealId, use fallback single query
      if (!filters?.contactId && !filters?.dealId) {
        let query = supabase
          .from('calls')
          .select(selectQuery)
          .not('scheduled_for', 'is', null)
          .order('scheduled_for', { ascending: true });

        query = applyCommonFilters(query, filters);

        const { data, error: fetchError } = await query;
        if (fetchError) throw fetchError;

        allCalls = (data || []).map(transformHandler);
      }

      // Sort by scheduled_for
      allCalls.sort((a, b) => 
        new Date(a.scheduled_for!).getTime() - new Date(b.scheduled_for!).getTime()
      );

      setScheduledCalls(allCalls as Call[]);
    } catch (err) {
      console.error('Error loading scheduled calls:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScheduledCalls();

    // Build filters for realtime subscription
    const realtimeFilters: string[] = [];
    
    if (filters?.contactId) {
      realtimeFilters.push(`contact_id=eq.${filters.contactId}`);
    }
    if (filters?.dealId) {
      realtimeFilters.push(`deal_id=eq.${filters.dealId}`);
    }
    if (filters?.leadId) {
      realtimeFilters.push(`lead_id=eq.${filters.leadId}`);
    }

    // If no specific filters, don't subscribe to avoid global updates
    if (realtimeFilters.length === 0) {
      return;
    }

    // Create channels for each filter (OR logic for realtime too)
    const channels = realtimeFilters.map((filter, index) => {
      return supabase
        .channel(`scheduled-calls-changes-${index}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'calls',
            filter
          },
          () => {
            console.log('Scheduled calls data changed, reloading...');
            loadScheduledCalls();
          }
        )
        .subscribe();
    });

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [JSON.stringify(filters)]);

  return {
    scheduledCalls,
    loading,
    error,
    loadScheduledCalls,
  };
}
